import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Stage, Layer, Rect, Circle, Group, Text, Line } from 'react-konva';
import type { Order, Table, Reservation, FloorFixture } from '../../types';
import { getTableSeatsUsed, isFloorPlanActiveTableOrder } from '../../lib/orderUtils';
import { orderCreatedAtMs } from '../../lib/orderTimestamps';
import { isReservationSoonForFloor, reservationHoldsTable } from '../../lib/reservationCapacity';
import TableDetailsModal from './TableDetailsModal';
import BuffetTimer from './BuffetTimer';

interface FloorPlanKonvaProps {
  tables: Table[];
  orders: Order[];
  reservations?: Reservation[];
  layoutComponents?: FloorFixture[];
  onTableClick: (tableId: number) => void;
  isEditMode?: boolean;
  isMergeMode?: boolean;
  selectedTableIds?: number[];
  onTableMove?: (tableId: number, x: number, y: number) => void;
  onLayoutComponentMove?: (componentId: string, x: number, y: number) => void;
  onTableSelect?: (tableId: number) => void;
  onSplitTable?: (tableId: number) => void;
  onTransferOrder?: (orderId: string, tableId: number) => void;
  selectedComponentId?: string | null;
  onSelectComponent?: (id: string | null) => void;
  showGrid?: boolean;
  gridSize?: number;
  snapToGrid?: boolean;
  businessTimeZone?: string;
}

// Fixed canvas dimensions for stable positioning
const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 800;

/** Live paint: order / seated / soon — not stale Firestore table.status (avoids red↔green blink). */
const getTableStatusColor = (
  table: Table,
  order: Order | undefined,
  reservation: Reservation | undefined,
): string => {
  if (order?.status === 'served') return '#3b82f6'; // blue
  if (order) return '#ef4444'; // red — active ticket
  if (reservation?.status === 'seated') return '#ef4444'; // red — seated, no ticket yet
  if (
    reservation &&
    (reservation.status === 'pending' || reservation.status === 'confirmed') &&
    isReservationSoonForFloor(reservation, new Date())
  ) {
    return '#eab308'; // yellow
  }
  return '#22c55e'; // green
};

// Helper to snap to grid
const snapToGridValue = (value: number, gridSize: number, enabled: boolean): number => {
  if (!enabled) return value;
  return Math.round(value / gridSize) * gridSize;
};

const FloorPlanKonva: React.FC<FloorPlanKonvaProps> = ({
  tables,
  orders,
  reservations = [],
  layoutComponents = [],
  onTableClick,
  isEditMode = false,
  isMergeMode = false,
  selectedTableIds = [],
  onTableMove,
  onLayoutComponentMove,
  onTableSelect,
  onSplitTable,
  onTransferOrder,
  selectedComponentId,
  onSelectComponent,
  showGrid = false,
  gridSize = 5,
  snapToGrid = false,
  businessTimeZone = 'Pacific/Auckland',
}) => {
  const stageRef = useRef<any>(null);
  const [selectedTableForDetails, setSelectedTableForDetails] = useState<Table | null>(null);
  const [containerSize, setContainerSize] = useState({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT });
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate container size and scale
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const maxWidth = container.clientWidth - 48; // Account for padding
        const maxHeight = container.clientHeight - 48;
        
        // Maintain aspect ratio
        const aspectRatio = CANVAS_WIDTH / CANVAS_HEIGHT;
        let width = maxWidth;
        let height = width / aspectRatio;
        
        if (height > maxHeight) {
          height = maxHeight;
          width = height * aspectRatio;
        }
        
        setContainerSize({ width, height });
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const today = new Date().toLocaleDateString('en-CA', { timeZone: businessTimeZone });
  const now = new Date();
  const activeReservations = reservations.filter(
    (res) =>
      (res.status === 'seated' && res.reservationDate === today) ||
      isReservationSoonForFloor(res, now),
  );

  // Render grid
  const renderGrid = () => {
    if (!showGrid) return null;
    const lines: JSX.Element[] = [];
    const step = (gridSize / 100) * CANVAS_WIDTH;
    
    // Vertical lines
    for (let x = 0; x <= CANVAS_WIDTH; x += step) {
      lines.push(
        <Line
          key={`v-${x}`}
          points={[x, 0, x, CANVAS_HEIGHT]}
          stroke="#d1d5db"
          strokeWidth={1}
          opacity={0.3}
          listening={false}
        />
      );
    }
    
    // Horizontal lines
    for (let y = 0; y <= CANVAS_HEIGHT; y += step) {
      lines.push(
        <Line
          key={`h-${y}`}
          points={[0, y, CANVAS_WIDTH, y]}
          stroke="#d1d5db"
          strokeWidth={1}
          opacity={0.3}
          listening={false}
        />
      );
    }
    
    return lines;
  };

  // Handle table drag end
  const handleTableDragEnd = useCallback((table: Table, e: any) => {
    if (!onTableMove) return;
    
    const newX = e.target.x();
    const newY = e.target.y();
    
    // Convert to percentage
    let xPercent = (newX / CANVAS_WIDTH) * 100;
    let yPercent = (newY / CANVAS_HEIGHT) * 100;
    
    // Snap to grid if enabled
    let snappedX = snapToGridValue(xPercent, gridSize, snapToGrid);
    let snappedY = snapToGridValue(yPercent, gridSize, snapToGrid);

    // Clamp to valid bounds 0–100 to prevent tables from going off-canvas
    snappedX = Math.max(0, Math.min(100, snappedX));
    snappedY = Math.max(0, Math.min(100, snappedY));
    
    // Update position
    onTableMove(table.id, snappedX, snappedY);
    
    // Reset position to snapped value
    e.target.x((snappedX / 100) * CANVAS_WIDTH);
    e.target.y((snappedY / 100) * CANVAS_HEIGHT);
  }, [onTableMove, gridSize, snapToGrid]);

  // Handle component drag end
  const handleComponentDragEnd = useCallback((component: FloorFixture, e: any) => {
    if (!onLayoutComponentMove) return;
    
    const newX = e.target.x();
    const newY = e.target.y();

    // Convert to percentage
    let xPercent = (newX / CANVAS_WIDTH) * 100;
    let yPercent = (newY / CANVAS_HEIGHT) * 100;
    
    // Snap to grid if enabled
    let snappedX = snapToGridValue(xPercent, gridSize, snapToGrid);
    let snappedY = snapToGridValue(yPercent, gridSize, snapToGrid);

    // Clamp to valid bounds 0–100 so fixtures don't disappear
    snappedX = Math.max(0, Math.min(100, snappedX));
    snappedY = Math.max(0, Math.min(100, snappedY));
    
    // Update position
    onLayoutComponentMove(component.id, snappedX, snappedY);
    
    // Reset position to snapped value
    e.target.x((snappedX / 100) * CANVAS_WIDTH);
    e.target.y((snappedY / 100) * CANVAS_HEIGHT);
  }, [onLayoutComponentMove, gridSize, snapToGrid]);

  // Render layout components
  const renderLayoutComponents = () => {
    return layoutComponents.map(component => {
      const x = (component.x / 100) * CANVAS_WIDTH;
      const y = (component.y / 100) * CANVAS_HEIGHT;
      const width = (component.width / 100) * CANVAS_WIDTH;
      const height = (component.height / 100) * CANVAS_HEIGHT;
      const isSelected = selectedComponentId === component.id;
      
      const componentColors: Record<FloorFixture['type'], string> = {
        entrance: '#e5e7eb',
        bar: '#92400e',
        patio: '#d1fae5',
        kitchen: '#fed7aa',
        restroom: '#dbeafe',
        storage: '#d1d5db',
        counter: '#fef3c7',
        window: '#e0f2fe',
        wall: '#9ca3af',
        custom: '#e9d5ff',
      };
      
      const color = componentColors[component.type] || '#e9d5ff';
      
      return (
        <Group
          key={component.id}
          x={x}
          y={y}
          draggable={isEditMode}
          onDragEnd={(e) => handleComponentDragEnd(component, e)}
          onClick={() => onSelectComponent && onSelectComponent(component.id)}
          onTap={() => onSelectComponent && onSelectComponent(component.id)}
        >
          <Rect
            width={width}
            height={height}
            fill={color}
            stroke={isSelected ? '#ec4899' : '#6b7280'}
            strokeWidth={isSelected ? 3 : 1}
            cornerRadius={component.type === 'wall' ? 0 : 4}
            offsetX={width / 2}
            offsetY={height / 2}
            rotation={component.rotation || 0}
          />
          <Text
            text={component.label.toUpperCase()}
            fontSize={12}
            fontStyle="bold"
            fill="#374151"
            align="center"
            verticalAlign="middle"
            width={width}
            height={height}
            offsetX={width / 2}
            offsetY={height / 2}
            rotation={component.rotation || 0}
          />
        </Group>
      );
    });
  };

  // Render tables
  const renderTables = () => {
    return tables.map((table, tableIndex) => {
      const tableOrders = orders.filter((o) => isFloorPlanActiveTableOrder(o, Number(table.id), businessTimeZone));
      // Prefer a served order for table color (blue); otherwise first order
      const order = tableOrders.find(o => o.status === 'served') ?? tableOrders[0];
      const customerCount = tableOrders.length;
      const seatsUsed = getTableSeatsUsed(tableOrders);
      const spareSeats = Math.max(0, table.capacity - seatsUsed);
      const tableReservations = activeReservations
        .filter((r) => reservationHoldsTable(r, table.id))
        .sort((a, b) => {
          if (a.status === 'seated' && b.status !== 'seated') return -1;
          if (b.status === 'seated' && a.status !== 'seated') return 1;
          const dateCompare = a.reservationDate.localeCompare(b.reservationDate);
          if (dateCompare !== 0) return dateCompare;
          return a.reservationTime.localeCompare(b.reservationTime);
        });
      const reservation = tableReservations[0];
      
      // Use numeric comparison so Firestore string ids (e.g. "5") match number 5
      const tableIdNum = Number(table.id);
      const isMerged = table.mergedWith && Array.isArray(table.mergedWith) && table.mergedWith.length > 1;
      const primaryId = table.mergedWith?.[0];
      const isPrimaryMerged = isMerged && primaryId != null && Number(primaryId) === tableIdNum;
      const shouldShow = !isMerged || isPrimaryMerged;

      if (!shouldShow) {
        return null;
      }

      // Safely normalize and clamp coordinates so no table goes out of bounds
      // Handle various coordinate formats and edge cases
      let safeXPercent = 50; // Default to center
      let safeYPercent = 50; // Default to center
      
      if (typeof table.x === 'number' && isFinite(table.x) && !isNaN(table.x)) {
        safeXPercent = Math.max(0, Math.min(100, table.x));
      } else if (table.x !== undefined && table.x !== null) {
        // Try to parse if it's a string
        const parsedX = parseFloat(String(table.x));
        if (!isNaN(parsedX) && isFinite(parsedX)) {
          safeXPercent = Math.max(0, Math.min(100, parsedX));
        }
      }
      
      if (typeof table.y === 'number' && isFinite(table.y) && !isNaN(table.y)) {
        safeYPercent = Math.max(0, Math.min(100, table.y));
      } else if (table.y !== undefined && table.y !== null) {
        // Try to parse if it's a string
        const parsedY = parseFloat(String(table.y));
        if (!isNaN(parsedY) && isFinite(parsedY)) {
          safeYPercent = Math.max(0, Math.min(100, parsedY));
        }
      }
      
      const x = (safeXPercent / 100) * CANVAS_WIDTH;
      const y = (safeYPercent / 100) * CANVAS_HEIGHT;
      
      // Ensure table is always visible - if coordinates are invalid, use a safe position
      // This prevents tables from disappearing off-screen
      const finalX = isFinite(x) && !isNaN(x) ? x : CANVAS_WIDTH / 2;
      const finalY = isFinite(y) && !isNaN(y) ? y : CANVAS_HEIGHT / 2;
      
      const isSelected = selectedTableIds.includes(table.id);
      const color = getTableStatusColor(table, order, reservation);
      const isSquare = (table.shape || 'square') === 'square';
      const size = isSquare ? { width: 128, height: 88 } : { width: 96, height: 96 };
      
      // Calculate elapsed time for order
      let elapsedTimeText = '';
      if (order) {
        const placedMs = orderCreatedAtMs(order);
        const diff = Date.now() - (placedMs > 0 ? placedMs : Date.now());
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        elapsedTimeText = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
      
      return (
        <Group
          key={`table-${(table as { firestoreId?: string }).firestoreId ?? table.id}-${tableIndex}`}
          x={finalX}
          y={finalY}
          draggable={isEditMode}
          onDragEnd={(e) => handleTableDragEnd(table, e)}
          onClick={() => {
            if (isMergeMode && onTableSelect) {
              onTableSelect(table.id);
            } else if (!isEditMode) {
              onTableClick(table.id);
            }
          }}
          onTap={() => {
            if (isMergeMode && onTableSelect) {
              onTableSelect(table.id);
            } else if (!isEditMode) {
              onTableClick(table.id);
            }
          }}
          onDblClick={() => {
            if (!isEditMode && !isMergeMode) {
              setSelectedTableForDetails(table);
            }
          }}
        >
          {/* Background with gradient effect */}
          {isSquare ? (
            <>
              <Rect
                width={size.width}
                height={size.height}
                fill={color}
                stroke={isSelected ? '#3b82f6' : isMerged && isPrimaryMerged ? '#a855f7' : '#ffffff'}
                strokeWidth={isSelected ? 4 : isMerged && isPrimaryMerged ? 3 : 2.5}
                cornerRadius={8}
                shadowBlur={8}
                shadowColor="rgba(0,0,0,0.4)"
                shadowOffsetY={2}
                offsetX={size.width / 2}
                offsetY={size.height / 2}
              />
              {/* Highlight overlay for depth */}
              <Rect
                width={size.width}
                height={size.height * 0.35}
                fill="rgba(255,255,255,0.25)"
                cornerRadius={8}
                offsetX={size.width / 2}
                offsetY={size.height / 2 - size.height * 0.325}
              />
            </>
          ) : (
            <>
              <Circle
                radius={size.width / 2}
                fill={color}
                stroke={isSelected ? '#3b82f6' : isMerged && isPrimaryMerged ? '#a855f7' : '#ffffff'}
                strokeWidth={isSelected ? 4 : isMerged && isPrimaryMerged ? 3 : 2.5}
                shadowBlur={8}
                shadowColor="rgba(0,0,0,0.4)"
                shadowOffsetY={2}
              />
              {/* Highlight overlay for depth */}
              <Circle
                radius={size.width / 2 - 2}
                fill="rgba(255,255,255,0.2)"
                offsetY={-size.width / 6}
              />
            </>
          )}
          
          {/* Table name: constrained width so it stays inside the table */}
          <Text
            text={table.name}
            x={-size.width * 0.44}
            width={size.width * 0.88}
            fontSize={13}
            fontStyle="bold"
            fontFamily="Arial, sans-serif"
            fill="#000000"
            align="center"
            verticalAlign="middle"
            offsetY={-size.height / 2 + 6}
            wrap="none"
            ellipsis={true}
            shadowBlur={3}
            shadowColor="rgba(255,255,255,0.9)"
            shadowOffsetY={1}
            shadowOffsetX={0}
            listening={false}
          />
          
          {/* Merged indicator */}
          {isMerged && isPrimaryMerged && (
            <>
              <Rect
                width={size.width * 0.75}
                height={16}
                fill="#a855f7"
                cornerRadius={3}
                offsetX={size.width * 0.375}
                offsetY={-size.height / 2 + 20}
                shadowBlur={3}
                shadowColor="rgba(0,0,0,0.3)"
              />
              <Text
                text="MERGED"
                fontSize={9}
                fontStyle="bold"
                fill="#ffffff"
                align="center"
                offsetY={-size.height / 2 + 20}
                shadowBlur={1}
                shadowColor="rgba(0,0,0,0.5)"
              />
            </>
          )}
          
          {/* Capacity badge: show seats used/capacity when there are orders, else capacity only */}
          <Group offsetY={size.height / 2 - 32}>
            <Circle 
              radius={12} 
              fill="rgba(255,255,255,0.25)" 
              stroke="#ffffff"
              strokeWidth={1.5}
            />
            <Text
              text="👥"
              fontSize={14}
              align="center"
              offsetY={-7}
            />
            <Text
              text={seatsUsed > 0 ? `${seatsUsed}/${table.capacity}` : table.capacity.toString()}
              fontSize={11}
              fontStyle="bold"
              fill="#ffffff"
              align="center"
              offsetY={10}
              shadowBlur={2}
              shadowColor="rgba(0,0,0,0.6)"
            />
          </Group>
          {/* Spare seats indicator (below capacity badge) */}
          {spareSeats > 0 && (
            <Group offsetY={size.height / 2 - 14}>
              <Text
                text={`${spareSeats} spare`}
                fontSize={8}
                fontStyle="bold"
                fill="#bbf7d0"
                align="center"
                listening={false}
              />
            </Group>
          )}
          
          {/* Customer count badge */}
          {customerCount > 1 && (
            <Group offsetY={size.height / 2 - 8}>
              <Rect
                width={size.width * 0.85}
                height={16}
                fill="rgba(251, 191, 36, 0.9)"
                cornerRadius={3}
                offsetX={size.width * 0.425}
                offsetY={8}
                shadowBlur={2}
                shadowColor="rgba(0,0,0,0.3)"
              />
              <Text
                text={`${customerCount} Customers`}
                fontSize={9}
                fontStyle="bold"
                fill="#78350f"
                align="center"
                offsetY={8}
              />
            </Group>
          )}
          
          {/* Serving person (small text below table name) */}
          {order?.servingStaffName && (
            <Text
              text={order.servingStaffName}
              fontSize={9}
              fill="rgba(0,0,0,0.85)"
              align="center"
              offsetY={-size.height / 2 + 22}
              shadowBlur={1}
              shadowColor="rgba(255,255,255,0.8)"
            />
          )}
          
          {/* Elapsed time badge */}
          {elapsedTimeText && (
            <Group offsetY={size.height / 2 + (customerCount > 1 ? 12 : 4)}>
              <Rect
                width={size.width * 0.85}
                height={18}
                fill="rgba(0,0,0,0.4)"
                cornerRadius={4}
                offsetX={size.width * 0.425}
                offsetY={9}
                shadowBlur={2}
                shadowColor="rgba(0,0,0,0.3)"
              />
              <Text
                text={`⏱ ${elapsedTimeText}`}
                fontSize={10}
                fontStyle="bold"
                fill="#ffffff"
                align="center"
                offsetY={9}
                shadowBlur={1}
                shadowColor="rgba(0,0,0,0.5)"
              />
            </Group>
          )}
          
          {/* Buffet timer badge */}
          {order?.orderType === 'buffet' && order.buffetEndTime && (
            <Group offsetY={size.height / 2 + (elapsedTimeText ? 34 : (customerCount > 1 ? 22 : 14))}>
              <Rect
                width={size.width * 0.75}
                height={16}
                fill="rgba(139, 92, 246, 0.9)"
                cornerRadius={3}
                offsetX={size.width * 0.375}
                offsetY={8}
                shadowBlur={2}
                shadowColor="rgba(0,0,0,0.3)"
              />
              <Text
                text={`⏰ ${Math.max(0, Math.floor((order.buffetEndTime - Date.now()) / 60000))}m`}
                fontSize={9}
                fontStyle="bold"
                fill="#ffffff"
                align="center"
                offsetY={8}
                shadowBlur={1}
                shadowColor="rgba(0,0,0,0.5)"
              />
            </Group>
          )}
        </Group>
      );
    });
  };

  return (
    <>
      <div ref={containerRef} className="flex-1 overflow-auto p-6 bg-gray-100">
        <div className="flex items-center justify-center h-full">
          <Stage
            ref={stageRef}
            width={containerSize.width}
            height={containerSize.height}
            scaleX={containerSize.width / CANVAS_WIDTH}
            scaleY={containerSize.height / CANVAS_HEIGHT}
            style={{ background: '#ffffff', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          >
            <Layer>
              {/* Background walls */}
              <Rect x={0} y={0} width={16} height={CANVAS_HEIGHT} fill="#e5e7eb" listening={false} />
              <Rect x={CANVAS_WIDTH - 16} y={0} width={16} height={CANVAS_HEIGHT} fill="#e5e7eb" listening={false} />
              <Rect x={0} y={CANVAS_HEIGHT - 16} width={CANVAS_WIDTH} height={16} fill="#e5e7eb" listening={false} />
              
              {/* Grid */}
              {renderGrid()}
              
              {/* Layout Components */}
              {renderLayoutComponents()}
              
              {/* Tables */}
              {renderTables()}
            </Layer>
          </Stage>
        </div>
      </div>
      {selectedTableForDetails && (
        <TableDetailsModal
          table={selectedTableForDetails}
          orders={orders}
          tables={tables}
          reservations={reservations}
          onClose={() => setSelectedTableForDetails(null)}
        />
      )}
    </>
  );
};

export default FloorPlanKonva;
